import React, {useContext, useEffect, useState} from "react";
import {Table, Spin, message, Button} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import {
    StripeInvoiceItem
} from "../../api-client";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {getStripeApi} from "../../api/apiInstances";
import logger from "../../utils/logger";


const StripeInvoices = () => {
    const invoicesApi = getStripeApi();
    const {principal} = useContext(DentobookApplicationContext);
    const [invoices, setInvoices] = useState<StripeInvoiceItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const intl = useIntl();


    const loadData = async () => {
        setLoading(true);
        try {
            const response = await invoicesApi.fetchStripeInvoices(principal?.user?.organizationId as string);
            setInvoices(response.data);
        } catch (error: any) {
            logger.error("Error fetching invoices: ", error);
            const errorMessage = error?.response?.data?.message;
            if (
                error?.response?.status === 404 ||
                errorMessage?.includes("Organization could not be found in stripe_customers table")
            ) {
                setInvoices([]);
            } else {
                message.error(intl.formatMessage({
                    id: "fetch_invoices_error",
                    defaultMessage: "Failed to fetch invoices"
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const formatDate = (isoDate: string): string => {
        const date = new Date(isoDate);
        const day = date.getUTCDate().toString().padStart(2, "0");
        const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
        const year = date.getUTCFullYear();
        return `${day}.${month}.${year}`;
    };

    return (
        <div>
            {loading ? (
                <div style={{ textAlign: "center", padding: "100px 0" }}>
                    <Spin tip={intl.formatMessage({ id: "loading", defaultMessage: "Loading..." })} />
                </div>
            ) : (
                <Table dataSource={invoices} loading={loading} rowKey="id" pagination={false}>
                    <Table.Column
                        title={<FormattedMessage id="status" defaultMessage="Status" />}
                        dataIndex="status"
                        key="status"
                    />
                    <Table.Column
                        title={<FormattedMessage id="date" defaultMessage="Invoice Date" />}
                        dataIndex="created"
                        key="created"
                        render={(created: string) => formatDate(created)}
                    />
                    <Table.Column
                        title={<FormattedMessage id="hosted_invoice_url" defaultMessage="Hosted Invoice URL" />}
                        dataIndex="hostedInvoiceUrl"
                        key="hostedInvoiceUrl"
                        render={(url: string) =>
                            url ? (
                                <Button
                                    type="primary"
                                    style={{ marginRight: 4 }}
                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                >
                                    <FormattedMessage id="view_hosted_invoice" defaultMessage="View Hosted Invoice" />
                                </Button>
                            ) : null
                        }
                    />

                    <Table.Column
                        title={<FormattedMessage id="invoice_pdf" defaultMessage="Invoice PDF" />}
                        dataIndex="invoicePdf"
                        key="invoicePdf"
                        render={(url: string) =>
                            url ? (
                                <Button
                                    type="dashed"
                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                >
                                    <FormattedMessage id="download_invoice_pdf" defaultMessage="Download Invoice PDF" />
                                </Button>
                            ) : null
                        }
                    />
                    <Table.Column
                        title={<FormattedMessage id="amount_paid" defaultMessage="Amount Paid" />}
                        dataIndex="amountPaid"
                        key="amountPaid"
                        render={(value: number) =>
                            // Assuming the amount is in cents; adjust currency as needed.
                            (value / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" })
                        }
                    />
                </Table>
            )}
        </div>
    );
};

export default StripeInvoices;
